import { computed, ref, Ref } from 'vue';
import { instanceToInstance } from 'class-transformer';
import { Roomgroup } from '@/models';
import { InspectionPoint } from '@/models/inspectionPoint/InspectionPoint';
import {
  InspectionDevice,
  InspectionDeviceFactory,
} from '@/models/inspectionPoint/InspectionDevice';
import { DeviceTypeEnum } from '@/enums/device';
import { roomgroupNotAccessibleReasonsList } from '@/enums/roomgroup';
import { ImpedimentType } from '@/enums/generic';
import { useValidation } from '@/composables/useValidation';
import useStores from '@/composables/useStores';
import ImageCapture from '@/components/Forms/DeviceFormComponents/ImageCapture.vue';
import { useRoomStore } from '@/store/entities/roomStore';

export interface StaircaseTestFormOptions {
  targetRoomGroupId: string;
}

export interface StaircaseTestFormResult {
  targetRoomgroup: Ref<Roomgroup>;
  inspectionPoint: Ref<InspectionPoint>;
  device: Ref<InspectionDevice>;
  imageCaptureRef: Ref<InstanceType<typeof ImageCapture> | null>;
  v$: ReturnType<typeof useValidation>['v$'];
  isFormValid: Ref<boolean>;
  hasImage: Ref<boolean>;
  handleSubmit: () => void;
}

export interface StaircaseTestEntities {
  roomgroup: Roomgroup;
  point: InspectionPoint;
  inspectionDevice: InspectionDevice;
}

export function getFirstRoomOfRoomGroup(roomGroupId: string) {
  return useRoomStore().getFirstRoomOfRoomgroup(roomGroupId);
}

export function findExistingStaircaseTest(
  roomGroupId: string,
  inspectionPointStore: ReturnType<typeof useStores>['inspectionPointStore']
): InspectionPoint | undefined {
  const firstRoom = getFirstRoomOfRoomGroup(roomGroupId);
  if (!firstRoom) return undefined;

  const inspectionPointsInRoom =
    inspectionPointStore.getInspectionPointsByRoomId(firstRoom.id);
  return inspectionPointsInRoom.find((ip) => ip.isRemoteTest);
}

export function createStaircaseTestEntities(roomId: string): {
  point: InspectionPoint;
  inspectionDevice: InspectionDevice;
} {
  const point = new InspectionPoint(roomId);
  point.isRemoteTest = true;
  point.setSupportedDeviceType(DeviceTypeEnum.SMART_RADIATOR_THERMOSTAT);

  const inspectionDevice = InspectionDeviceFactory.createDevice(
    DeviceTypeEnum.SMART_RADIATOR_THERMOSTAT
  );
  inspectionDevice.setInspectionPointId(point.id);
  point.activeTestDeviceId = inspectionDevice.id;

  return { point, inspectionDevice };
}

export function setDefaultInaccessible(roomgroup: Roomgroup): void {
  if (!roomgroup.reasonNotAccessible) {
    roomgroup.reasonNotAccessible = roomgroupNotAccessibleReasonsList[1].value;
    roomgroup.dateNotAccessible = new Date().toISOString();
    roomgroup.typeNotAccessible = ImpedimentType.TEMPORARY;
  }
}

export function initializeStaircaseTestEntities(
  targetRoomGroupId: string,
  entitiesStore: ReturnType<typeof useStores>['entitiesStore'],
  inspectionPointStore: ReturnType<typeof useStores>['inspectionPointStore']
): StaircaseTestEntities {
  const roomgroupFromStore = entitiesStore.getEntityById(
    targetRoomGroupId
  ) as Roomgroup;
  const roomgroup = instanceToInstance(roomgroupFromStore);

  setDefaultInaccessible(roomgroup);

  const existingInspectionPoint = findExistingStaircaseTest(
    targetRoomGroupId,
    inspectionPointStore
  );

  if (existingInspectionPoint) {
    const existingDevice = existingInspectionPoint.activeTestDeviceId
      ? (entitiesStore.getEntityById(
          existingInspectionPoint.activeTestDeviceId
        ) as InspectionDevice)
      : undefined;

    const point = instanceToInstance(existingInspectionPoint);

    let inspectionDevice: InspectionDevice;
    if (existingDevice) {
      inspectionDevice = instanceToInstance(existingDevice);
    } else {
      inspectionDevice = InspectionDeviceFactory.createDevice(
        DeviceTypeEnum.SMART_RADIATOR_THERMOSTAT
      );
      inspectionDevice.setInspectionPointId(point.id);
      point.activeTestDeviceId = inspectionDevice.id;
    }

    return {
      roomgroup,
      point,
      inspectionDevice,
    };
  }

  const firstRoom = getFirstRoomOfRoomGroup(targetRoomGroupId);
  if (!firstRoom) {
    throw new Error('Target roomgroup has no rooms');
  }

  const { point, inspectionDevice } = createStaircaseTestEntities(firstRoom.id);

  return { roomgroup, point, inspectionDevice };
}

export function useStaircaseTestForm(
  options: StaircaseTestFormOptions,
  emit: (event: 'update-entity' | 'update-entities', ...args: unknown[]) => void
): StaircaseTestFormResult {
  const { entitiesStore, roomStore, inspectionPointStore } = useStores();

  const imageCaptureRef = ref<InstanceType<typeof ImageCapture> | null>(null);

  const entities = initializeStaircaseTestEntities(
    options.targetRoomGroupId,
    entitiesStore,
    inspectionPointStore
  );

  const inspectionPoint = ref<InspectionPoint>(entities.point);
  const device = ref<InspectionDevice>(entities.inspectionDevice);
  const targetRoomgroup = ref<Roomgroup>(entities.roomgroup);

  const { v$, isFormValid: isFieldsValid } = useValidation(
    { device, roomgroup: targetRoomgroup },
    { isOnSiteInspection: true }
  );

  const hasImage = computed(() => imageCaptureRef.value?.hasImage ?? false);
  const isFormValid = computed(() => isFieldsValid.value && hasImage.value);

  const getEntitiesToSave = (): (
    | InspectionDevice
    | InspectionPoint
    | Roomgroup
  )[] => {
    return [device.value, inspectionPoint.value, targetRoomgroup.value];
  };

  const handleSubmit = () => {
    if (!inspectionPoint.value || !device.value) {
      throw new Error('Form not initialized properly');
    }

    if (targetRoomgroup.value) {
      targetRoomgroup.value.setIsAccessible(false);
    }

    if (imageCaptureRef.value?.hasImage) {
      imageCaptureRef.value.saveImage();
    }

    emit('update-entities', getEntitiesToSave());
  };

  return {
    targetRoomgroup,
    inspectionPoint,
    device,
    imageCaptureRef,
    v$,
    isFormValid,
    hasImage,
    handleSubmit,
  };
}
